import { ChangeDetectionStrategy, Component, forwardRef, inject, input, output, signal } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { DndDirective } from '../../../../directives/dnd-directive';
import { MatIcon } from '@angular/material/icon';
import { ImageCropperDialogComponent } from '../../../dialog/image-cropper-dialog/image-cropper-dialog.component';
import { DialogUtils } from '../../../../utils/dialog-utils';
import { MatDialog } from '@angular/material/dialog';
import { FileUtils } from '../../../../utils/file-utils';

@Component({
  selector: 'lib-file-upload-input',
  templateUrl: './file-upload-input.component.html',
  styleUrls: ['./file-upload-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileUploadInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => FileUploadInputComponent),
      multi: true,
    },
  ],
  imports: [DndDirective, MatIcon],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileUploadInputComponent implements ControlValueAccessor, Validator {
  private readonly dialog = inject(MatDialog);
  protected readonly FileUtils = FileUtils;
  multiple = input<boolean>(false);
  error = input<boolean>(false);
  accept = input<string>('image/png, image/jpeg, application/pdf');
  description = input.required<string>();
  controlId = input.required<string>();
  showCropDialog = input<boolean>(false);

  singleFileOnlyError = input<string | null>(null);

  selectedFilesChange = output<File[]>();
  public files = signal<File[]>([]);
  public disabled = signal(false);
  private singleFileOnlyValidationError = signal<string | null>(null);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public onChange = (value: File[]) => {
    //
  };

  public onTouched = () => {
    //
  };

  public writeValue(value: File[]): void {
    if (value !== undefined) {
      this.files.set(value);
      this.singleFileOnlyValidationError.set(null);
    }
  }

  public registerOnChange(fn: (files: File[]) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public validate(control: AbstractControl<File[]>): ValidationErrors | null {
    if (this.singleFileOnlyValidationError()) {
      return { singleFileOnly: this.singleFileOnlyValidationError() };
    }

    return null;
  }

  public handleDragAndDrop(event: FileList): void {
    if (this.disabled()) return;
    const acceptedTypes = this.accept();
    const fileList = Array.from(event).filter(file => acceptedTypes.includes(file.type));
    if (!fileList.length) return;
    if (!this.multiple() && fileList.length > 1) {
      this.setSingleFileOnlyValidationError(true);
      this.onTouched();
      this.onValidatorChange();
      return;
    }
    this.setSingleFileOnlyValidationError(false);

    if (this.showCropDialog()) {
      const dialogRef = this.dialog.open(ImageCropperDialogComponent, DialogUtils.smallDialogConfig());
      dialogRef.componentInstance.imageFiles.set([...fileList]);

      dialogRef.afterClosed().subscribe((result: File[] | null) => {
        // in case the user cancelled the modal then we just get `null` back
        if (result) {
          this.files.update(fileArray => [...fileArray, ...result]);
          this.onChange(this.files());
          this.onTouched();
          this.selectedFilesChange.emit(this.files());
        }
      });
    } else {
      this.files.update(fileArray => [...fileArray, ...fileList]);
      this.onChange(this.files());
      this.onTouched();
      this.selectedFilesChange.emit(this.files());
    }
  }

  public handleInputChange(event: Event): void {
    if (this.disabled()) return;
    const inputElement = event.target as HTMLInputElement;
    const fileList = Array.from(inputElement.files ?? []);
    if (!fileList.length) return;
    if (!this.multiple() && fileList.length > 1) {
      this.setSingleFileOnlyValidationError(true);
      this.onTouched();
      this.onValidatorChange();
      inputElement.value = '';
      return;
    }
    this.setSingleFileOnlyValidationError(false);

    if (this.showCropDialog()) {
      const dialogRef = this.dialog.open(ImageCropperDialogComponent, DialogUtils.smallDialogConfig());
      dialogRef.componentInstance.imageFiles.set([...fileList]);

      dialogRef.afterClosed().subscribe((result: File[] | null) => {
        // in case the user cancelled the modal then we just get `null` back
        if (result) {
          this.files.update(fileArray => [...fileArray, ...result]);
          this.onChange(this.files());
          this.onTouched();
          this.selectedFilesChange.emit(this.files());
        } else {
          inputElement.files = null;
        }
      });
    } else {
      this.files.update(fileArray => [...fileArray, ...fileList]);
      this.onChange(this.files());
      this.onTouched();
      this.selectedFilesChange.emit(this.files());
    }
    inputElement.value = '';
  }

  private onValidatorChange: () => void = () => {
    //
  };

  private setSingleFileOnlyValidationError(hasViolation: boolean): void {
    this.singleFileOnlyValidationError.set(
      hasViolation ? (this.singleFileOnlyError() ?? $localize`Files must be uploaded 1 at a time`) : null,
    );
  }
}
